import { useState, useCallback, useRef } from 'react';
import { 
  Upload, X, CheckCircle, XCircle, Clock, FileText, 
  Image, File, Download, Trash2, AlertCircle, Eye
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badge component
const ProofStatusBadge = ({ status }) => {
  const config = {
    pending: { color: '#F59E0B', bg: 'bg-amber-500/20', icon: Clock, label: 'PENDING REVIEW' },
    approved: { color: '#10B981', bg: 'bg-emerald-500/20', icon: CheckCircle, label: 'APPROVED' },
    rejected: { color: '#EF4444', bg: 'bg-red-500/20', icon: XCircle, label: 'REJECTED' }
  };
  
  const { color, bg, icon: Icon, label } = config[status] || config.pending;
  
  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-1 ${bg}`}>
      <Icon className="w-3.5 h-3.5" style={{ color }} />
      <span className="text-xs font-medium" style={{ color }}>{label}</span>
    </div>
  );
};

// File icon based on type
const FileIcon = ({ type }) => {
  if (type?.startsWith('image/')) {
    return <Image className="w-5 h-5 text-blue-400" />;
  }
  if (type === 'application/pdf') {
    return <FileText className="w-5 h-5 text-red-400" />;
  }
  return <File className="w-5 h-5 text-zinc-400" />;
};

// Proof Upload Modal
export const ProofUploadModal = ({ 
  isOpen, 
  onClose, 
  taskId, 
  checklistId, 
  itemId, 
  itemText,
  onSuccess 
}) => {
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  
  const token = localStorage.getItem('hive_token');
  
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Validate file size (10MB max)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      if (droppedFile.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setFile(droppedFile);
      setError(null);
    }
  };
  
  const handleUpload = async () => {
    if (!file) return;
    
    setIsUploading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('task_id', taskId);
      formData.append('checklist_id', checklistId);
      formData.append('checklist_item_id', itemId);
      if (description) {
        formData.append('description', description);
      }
      
      await axios.post(`${API}/build-system/proof/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      onSuccess?.();
      onClose();
      setFile(null);
      setDescription('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload proof');
    } finally {
      setIsUploading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-zinc-900 border border-zinc-800 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-white font-bold">Upload Proof of Completion</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Item context */}
          <div className="p-3 bg-zinc-800/50 border border-zinc-700">
            <p className="text-zinc-400 text-xs uppercase mb-1">For checklist item:</p>
            <p className="text-white text-sm">{itemText}</p>
          </div>
          
          {/* Drop zone */}
          <div
            className={`border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
              file ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-zinc-700 hover:border-zinc-500'
            }`}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".png,.jpg,.jpeg,.webp,.pdf,.doc,.docx"
              onChange={handleFileSelect}
            />
            
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileIcon type={file.type} />
                <div className="text-left">
                  <p className="text-white text-sm font-medium">{file.name}</p>
                  <p className="text-zinc-500 text-xs">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  className="p-1 text-zinc-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                <p className="text-white text-sm">Drop file here or click to browse</p>
                <p className="text-zinc-500 text-xs mt-1">
                  PNG, JPG, PDF, DOC (max 10MB)
                </p>
              </>
            )}
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-zinc-400 text-sm mb-1">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add any notes about this proof..."
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-white text-sm resize-none h-20"
            />
          </div>
          
          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
        </div>
        
        <div className="p-4 border-t border-zinc-800 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-zinc-800 text-white text-sm hover:bg-zinc-700"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || isUploading}
            className="px-4 py-2 bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#F5D47B] disabled:opacity-50 flex items-center gap-2"
          >
            {isUploading ? (
              <>
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload Proof
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Proof status display for checklist items
export const ProofStatusDisplay = ({ proof, onDelete, onDownload }) => {
  if (!proof) return null;
  
  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };
  
  return (
    <div className={`mt-2 p-3 border ${
      proof.status === 'approved' ? 'border-emerald-500/30 bg-emerald-500/5' :
      proof.status === 'rejected' ? 'border-red-500/30 bg-red-500/5' :
      'border-amber-500/30 bg-amber-500/5'
    }`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileIcon type={proof.file_type} />
          <div>
            <p className="text-white text-sm">{proof.file_name}</p>
            <p className="text-zinc-500 text-xs">
              Submitted {formatDate(proof.submitted_at)}
            </p>
          </div>
        </div>
        <ProofStatusBadge status={proof.status} />
      </div>
      
      {/* Rejection reason */}
      {proof.status === 'rejected' && proof.review_notes && (
        <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20">
          <p className="text-red-400 text-xs">
            <strong>Reason:</strong> {proof.review_notes}
          </p>
        </div>
      )}
      
      {/* Actions */}
      <div className="mt-2 flex items-center gap-2">
        <button
          onClick={onDownload}
          className="flex items-center gap-1 px-2 py-1 bg-zinc-800 text-zinc-300 text-xs hover:bg-zinc-700"
        >
          <Download className="w-3 h-3" /> Download
        </button>
        {proof.status === 'pending' && onDelete && (
          <button
            onClick={onDelete}
            className="flex items-center gap-1 px-2 py-1 bg-red-500/10 text-red-400 text-xs hover:bg-red-500/20"
          >
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        )}
      </div>
    </div>
  );
};

// Enhanced checklist item with proof support
export const ChecklistItemWithProof = ({ 
  item, 
  taskId,
  checklistId,
  checked, 
  onToggle,
  proof,
  onProofChange
}) => {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const token = localStorage.getItem('hive_token');
  
  const handleDelete = async () => {
    if (!proof?.proof_id) return;
    if (!window.confirm('Delete this proof submission?')) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/build-system/proof/me/${proof.proof_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onProofChange?.();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete proof');
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handleDownload = async () => {
    if (!proof?.proof_id) return;
    
    try {
      const response = await axios.get(`${API}/build-system/proof/download/${proof.proof_id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', proof.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Failed to download file');
    }
  };
  
  // Determine if proof is required and status
  const needsProof = item.proof_required && !proof;
  const hasApprovedProof = proof?.status === 'approved';
  const hasPendingProof = proof?.status === 'pending';
  const hasRejectedProof = proof?.status === 'rejected';
  
  return (
    <div className={`border-l-4 ${
      hasApprovedProof ? 'border-green-500 bg-green-500/5' :
      hasPendingProof ? 'border-amber-500 bg-amber-500/5' :
      hasRejectedProof ? 'border-red-500 bg-red-500/5' :
      checked ? 'border-green-500 bg-green-500/5' : 
      'border-white/20 bg-white/5'
    }`}>
      <div className="flex items-start gap-3 p-3">
        <button 
          onClick={onToggle} 
          className="mt-0.5"
          data-testid={`checklist-${item.id}`}
        >
          {checked || hasApprovedProof ? (
            <CheckCircle className="w-5 h-5 text-green-400" />
          ) : hasPendingProof ? (
            <Clock className="w-5 h-5 text-amber-400" />
          ) : (
            <div className="w-5 h-5 border-2 border-white/40 rounded hover:border-white/60" />
          )}
        </button>
        
        <div className="flex-1 min-w-0">
          <p className={`${checked || hasApprovedProof ? 'text-white/60 line-through' : 'text-white'}`}>
            {item.text}
          </p>
          
          {/* Upload button for items requiring proof */}
          {item.proof_required && !proof && (
            <button
              onClick={() => setUploadModalOpen(true)}
              className="mt-2 flex items-center gap-1 px-3 py-1.5 bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-medium hover:bg-[#D4AF37]/30"
              data-testid={`proof-upload-${item.id}`}
            >
              <Upload className="w-3.5 h-3.5" />
              Upload {item.proof_type || 'Proof'}
            </button>
          )}
          
          {/* Resubmit button for rejected proofs */}
          {hasRejectedProof && (
            <button
              onClick={() => setUploadModalOpen(true)}
              className="mt-2 flex items-center gap-1 px-3 py-1.5 bg-red-500/20 text-red-400 text-xs font-medium hover:bg-red-500/30"
            >
              <Upload className="w-3.5 h-3.5" />
              Resubmit Proof
            </button>
          )}
          
          {/* Proof status display */}
          {proof && (
            <ProofStatusDisplay 
              proof={proof}
              onDelete={hasPendingProof ? handleDelete : null}
              onDownload={handleDownload}
            />
          )}
        </div>
        
        {item.required && !hasApprovedProof && (
          <span className="text-xs text-red-400 uppercase flex-shrink-0">Required</span>
        )}
      </div>
      
      {/* Upload Modal */}
      <ProofUploadModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        taskId={taskId}
        checklistId={checklistId}
        itemId={item.id}
        itemText={item.text}
        onSuccess={onProofChange}
      />
    </div>
  );
};

export default ProofUploadModal;
